import {
  Transport,
  TransportHeaders,
  TransportRequest,
  TransportResponse,
  TransportUrl
} from "../../transport/Transport"
import { WorkspaceConfigDto } from "../dto"
import { WorkspaceConfigContext } from "./WorkspaceConfigContext"
import { ApiCallMetrics } from "../../metrics/monitoring/ApiCallMetrics"

export interface HttpWorkspaceFetcher {
  fetchIfModified(lastModified: string | null): Promise<WorkspaceConfigContext | null>
}

export abstract class AbstractHttpWorkspaceFetcher implements HttpWorkspaceFetcher {
  protected constructor(private readonly transport: Transport) {}
  async fetchIfModified(lastModified: string | null): Promise<WorkspaceConfigContext | null> {
    const request = this.createRequest(lastModified)
    const response = await this.execute(request)
    return this.handleResponse(response)
  }

  protected abstract createRequest(lastModified: string | null): TransportRequest

  private execute(request: TransportRequest): Promise<TransportResponse> {
    return ApiCallMetrics.record("get.workspace", () => this.transport.send(request))
  }

  private handleResponse(response: TransportResponse): WorkspaceConfigContext | null {
    if (response.isNotModified()) {
      return null
    }
    if (!response.isSuccessful()) {
      throw new Error(`Http status code: ${response.statusCode}`)
    }
    if (response.body === null) {
      throw new Error("Response body is empty")
    }
    const dto = JSON.parse(response.body) as WorkspaceConfigDto
    const modifiedAt = response.headers.get(TransportHeaders.LAST_MODIFIED)
    return WorkspaceConfigContext.of(dto, modifiedAt)
  }
}

export class BrowserHttpWorkspaceFetcher extends AbstractHttpWorkspaceFetcher {
  constructor(private readonly url: string, transport: Transport) {
    super(transport)
  }

  protected createRequest(lastModified: string | null): TransportRequest {
    const builder = TransportUrl.builder(this.url)
    if (lastModified !== null) {
      builder.addParam("ifModifiedSince", lastModified) // ifModifiedSince as a query param to avoid CORS preflight.
    }
    return TransportRequest.builder().method("GET").url(builder.build()).build()
  }
}

export class NodeHttpWorkspaceFetcher extends AbstractHttpWorkspaceFetcher {
  constructor(private readonly url: string, transport: Transport) {
    super(transport)
  }
  protected createRequest(lastModified: string | null): TransportRequest {
    const builder = TransportRequest.builder().method("GET").url(this.url)
    if (lastModified !== null) {
      builder.addHeader(TransportHeaders.IF_MODIFIED_SINCE, lastModified)
    }
    return builder.build()
  }
}
